<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-root-v-if -->
<script setup>
import { computed } from 'vue';
import { buildPriceRulerState } from 'dashboard/helper/lais/priceRuler';

const props = defineProps({
  value: { type: [Number, String], default: 0 },
  min: { type: [Number, String], default: 0 },
  max: { type: [Number, String], default: 0 },
  percentiles: { type: Object, default: () => ({}) },
  markerColor: { type: String, default: 'gray' },
  variant: { type: String, default: 'sale' },
  formatValue: { type: Function, default: value => String(value || 0) },
});

const ruler = computed(() =>
  buildPriceRulerState({
    value: props.value,
    min: props.min,
    max: props.max,
    percentiles: props.percentiles,
    markerColor: props.markerColor,
    variant: props.variant,
  })
);

const withSuffix = value => {
  const formatted = props.formatValue(value);
  const suffix = ruler.value.variantConfig?.currencySuffix;
  return suffix && formatted !== 'Sem dados'
    ? `${formatted}${suffix}`
    : formatted;
};

const percentileItems = computed(() =>
  [
    { key: 'p25', label: 'P25', helper: 'Inicio da faixa' },
    { key: 'p50', label: 'P50', helper: 'Mediana' },
    { key: 'p75', label: 'P75', helper: 'Fim da faixa' },
  ].map(item => ({
    ...item,
    value: ruler.value.percentiles?.[item.key],
  }))
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-root-v-if -->
  <section
    v-show="ruler.available"
    class="rounded-lais border border-lais-border-soft bg-white px-4 py-4"
    aria-label="Regua de preco estimado"
  >
    <div class="flex items-center justify-between gap-3">
      <div>
        <p class="text-[13px] font-semibold text-lais-text">Régua de preço</p>
        <p class="text-[12px] text-lais-text-muted">
          Faixa recomendada baseada nos comparáveis e histórico.
        </p>
      </div>
      <span
        class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-semibold"
        :class="ruler.markerColor.textClass"
      >
        {{ ruler.variantConfig.label }}
      </span>
    </div>

    <div class="relative mt-8 h-12">
      <div
        class="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-lais-bg-muted"
      />
      <div
        class="absolute top-1/2 h-2 -translate-y-1/2 rounded-full"
        :class="ruler.variantConfig.bandClass"
        :style="{
          left: `${ruler.positions.bandStart}%`,
          width: `${ruler.positions.bandWidth}%`,
        }"
      />
      <span
        class="absolute top-1/2 h-6 w-px -translate-x-1/2 -translate-y-1/2 bg-lais-text-muted"
        :style="{ left: `${ruler.positions.p50}%` }"
        :aria-label="`Mediana: ${withSuffix(ruler.percentiles.p50)}`"
      />
      <button
        type="button"
        class="absolute top-1/2 grid size-6 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 border-white shadow-lais-card ring-4"
        :class="ruler.markerColor.markerClass"
        :style="{ left: `${ruler.positions.marker}%` }"
        :aria-label="`Marcador principal: ${withSuffix(ruler.value)}`"
      />
    </div>

    <div
      class="mt-1 flex items-center justify-between gap-3 text-[12px] font-semibold text-lais-text-muted"
    >
      <span>{{ withSuffix(ruler.min) }}</span>
      <span>{{ withSuffix(ruler.max) }}</span>
    </div>

    <div class="mt-4 grid gap-2 sm:grid-cols-3">
      <div
        v-for="item in percentileItems"
        :key="item.key"
        class="rounded-lais-sm bg-lais-bg-muted px-3 py-2"
      >
        <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
          {{ item.label }}
        </p>
        <p class="mt-1 text-[13px] font-semibold text-lais-text">
          {{ withSuffix(item.value) }}
        </p>
        <p class="mt-0.5 text-[11px] text-lais-text-muted">
          {{ item.helper }}
        </p>
      </div>
    </div>
  </section>
</template>
